import type { ReactNode } from 'react';
import { Card, Flex, Grid, Typography, theme } from 'antd';

/**
 * As peças de que Configurações é feita.
 *
 * O padrão vem da Voa, e é o mesmo em toda tela de ajuste que se respeita: a
 * página é uma pilha de **seções**, cada seção é um cartão com título e uma
 * linha de explicação, e dentro dela vêm **linhas** - rótulo e descrição à
 * esquerda, o controle à direita, um fio separando uma da outra.
 *
 * O que isso resolve, e um `<Form>` não resolve: em ajustes, metade das linhas
 * não é campo. É um botão que abre outra coisa, um valor que só se lê, um
 * aviso. O rótulo à esquerda com a ação à direita acomoda os quatro casos com o
 * mesmo ritmo visual, e é por isso que ele virou o formato padrão do gênero.
 *
 * No celular a linha empilha: rótulo em cima, controle embaixo, alinhado à
 * esquerda. Com 390px, rótulo e controle lado a lado espremem os dois.
 */

/** Um cartão de ajustes: título, explicação e as linhas dentro. */
export function SecaoDeAjustes({
  titulo,
  descricao,
  acao,
  children,
}: {
  titulo: string;
  descricao?: string;
  /** Ação da seção inteira - o "Novo" que mora no canto do cabeçalho. */
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card styles={{ body: { padding: 24 } }}>
      <Flex align="flex-start" justify="space-between" gap={16} wrap>
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {titulo}
          </Typography.Title>
          {descricao ? (
            <Typography.Text type="secondary">{descricao}</Typography.Text>
          ) : null}
        </div>
        {acao}
      </Flex>

      <Flex vertical style={{ marginTop: 20 }}>
        {children}
      </Flex>
    </Card>
  );
}

/**
 * Uma linha da seção: o que é à esquerda, o que se faz à direita.
 *
 * O fio fica no topo de cada linha menos a primeira (`:first-child` via
 * `primeira`), e não embaixo de todas: fio embaixo da última encostaria na
 * borda do cartão e o desenho ficaria com dois traços paralelos.
 */
export function LinhaDeAjuste({
  titulo,
  descricao,
  primeira,
  children,
}: {
  titulo: ReactNode;
  descricao?: ReactNode;
  primeira?: boolean;
  /** O controle: campo, botão, tag, o que a linha oferecer. */
  children?: ReactNode;
}) {
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();
  const estreita = !telas.sm;

  return (
    <Flex
      vertical={estreita}
      align={estreita ? 'stretch' : 'center'}
      justify="space-between"
      gap={estreita ? 8 : 24}
      style={{
        paddingBlock: 16,
        borderTop: primeira ? undefined : `1px solid ${token.colorSplit}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <Typography.Text strong style={{ display: 'block' }}>
          {titulo}
        </Typography.Text>
        {descricao ? (
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {descricao}
          </Typography.Text>
        ) : null}
      </div>
      {children ? <div style={{ flexShrink: 0 }}>{children}</div> : null}
    </Flex>
  );
}

/**
 * Um fato, dito em uma frase, com o que importa em cor.
 *
 * É como a Voa mostra a situação do plano: ícone fino e apagado, frase inteira,
 * e o valor - o nome do plano, a data, o número - destacado na cor da marca.
 * Custa uma linha e diz mais que uma tabela de chave e valor, porque quem lê
 * não precisa montar a frase na cabeça.
 */
export function Fato({ icone, children }: { icone: ReactNode; children: ReactNode }) {
  const { token } = theme.useToken();

  return (
    <Flex align="flex-start" gap={10}>
      <span style={{ color: token.colorTextTertiary, lineHeight: token.lineHeight, marginTop: 1 }}>
        {icone}
      </span>
      <Typography.Text>{children}</Typography.Text>
    </Flex>
  );
}

/** O valor dentro do `Fato`: é o que o olho procura na frase. */
export function Valor({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();
  return <span style={{ color: token.colorPrimary }}>{children}</span>;
}

/** Cabeçalho de uma página de Configurações: título e a linha que o explica. */
export function TituloDaPagina({ titulo, descricao }: { titulo: string; descricao: string }) {
  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 4 }}>
        {titulo}
      </Typography.Title>
      <Typography.Text type="secondary">{descricao}</Typography.Text>
    </div>
  );
}
